import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Button, TextField, Box } from '@material-ui/core';
import { Card } from 'src/shared/ui/Card';
import type { WarboxCardProps } from 'src/shared/ui/Card/types';
import { useSnackbar } from 'notistack';
import { useTheme } from '@material-ui/core/styles';
import useLead from 'src/hooks/useLead';
import { Phone as CallIcon } from 'react-feather';
import { PhoneMissed as PhoneMissedIcon } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { handleGetPhoneAgencies } from 'src/modules/store/helpers';
import SITE_SETTINGS from 'src/constants/whiteLabel';
import useAuth from 'src/hooks/useAuth';
import { Call, Device } from '@twilio/voice-sdk';
import { removeTwilioFromMessage } from 'src/components/Lead/Calls/utils';
import { formatPhoneByCountryCode } from 'src/modules/phones/utils/formatPhoneByCountryCode';
import type { IServiceClient } from 'src/interfaces/serviceClient';
import { SERVICE_LEAD_TYPE } from 'src/constants/lead/leadTypes';
import { getServiceClientStore } from 'src/modules/serviceClients/helpers';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { USER_LEVEL_ACCESS } from 'src/components/access';

const types = ['first_call', 'follow_call'];

export type MakeCallServiceClientProps = Omit<WarboxCardProps, 'title' | 'intent'> & {
    client: IServiceClient | null;
    title?: never;
    intent?: never;
};

/**
 * Component to make a call to a service client.
 * Uses callToken and generateToken from useLead; does not use addComment.
 */
export const MakeCallServiceClient = ({
    client,
    ...props
}: MakeCallServiceClientProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { callToken, generateToken } = useLead();
    const { enqueueSnackbar } = useSnackbar();
    const [callStatus, setCallStatus] = useState('Calls.RegisteringDevice');
    const [typeCall, setTypeCall] = useState('first_call');
    const deviceRef = useRef<Device | null>(null);
    const callRef = useRef<Call | null>(null);
    const [isInCall, setIsInCall] = useState(false);
    const [currentCallerId, setCurrentCallerId] = useState('');

    const store = getServiceClientStore(client);

    const formatPhones = (phone: string) => {
        if (!phone || !client) return '';
        const normalizedPhone = phone.replace(/\+521/g, '+52').replace(/^521/, '52');
        return formatPhoneByCountryCode(
            normalizedPhone,
            client.countryCode,
            store?.countryCode || ECountryCode.Mexico
        );
    };

    useEffect(() => {
        if (store?._id) {
            generateToken(store._id);
        }
    }, [store?._id]);

    useEffect(() => {
        if (!client || !store?.areas) return;
        const { success, phone, callerId } = handleGetPhoneAgencies({
            area: SERVICE_LEAD_TYPE,
            areas: store.areas || [],
        });
        if (success) {
            const currentPhone = callerId || phone;
            const formattedPhone = formatPhones(currentPhone || '');
            if (user?.isVerifiedCallerId) {
                setCurrentCallerId(formatPhones(user?.phone || ''));
            } else if (client.agent?.isVerifiedCallerId) {
                setCurrentCallerId(formatPhones(client.agent?.phone || ''));
            } else {
                setCurrentCallerId(formattedPhone);
            }
        }
    }, [client]);

    const handleSetupDevice = () => {
        try {
            if (!deviceRef.current && callToken) {
                const device = new Device(callToken, {
                    codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
                });
                deviceRef.current = device;
                device.register();
                device.on('registering', () => setCallStatus('Calls.RegisteringDevice'));
                device.on('registered', () => setCallStatus('Calls.RegisteredDevice'));
                device.on('tokenWillExpire', () => store?._id && generateToken(store._id));
                device.on('error', (error) => {
                    setCallStatus('Error: ' + removeTwilioFromMessage(error.message));
                });
            }
        } catch (error) {
            if (error instanceof Error) {
                setCallStatus('Error: ' + removeTwilioFromMessage(error.message));
            }
        }
    };

    useEffect(() => {
        if (callToken) handleSetupDevice();
    }, [callToken]);

    const callCustomer = async () => {
        if (!client?.agent) {
            enqueueSnackbar(t('SnackBar.NoAgent'), { variant: 'error' });
            return;
        }
        if (deviceRef.current && store) {
            const formattedCallerId = formatPhones(currentCallerId);
            const params = {
                user: user._id,
                from: formattedCallerId,
                area: SERVICE_LEAD_TYPE,
                To: formatPhoneByCountryCode(client.phone, client.countryCode, store?.countryCode || ECountryCode.Mexico),
                lead: client._id,
                serviceClient: client._id,
                typeCall,
                callerId: formattedCallerId,
                store: store?._id || '',
                client: SITE_SETTINGS.client,
                record: store.isRecording ? 'record-from-answer' : 'do-not-record',
                assignedBy: user.accessLevel === USER_LEVEL_ACCESS ? user._id : null,
                type: 'dpx',
            };

            setCallStatus('Calls.Ringing');
            callRef.current = await deviceRef.current.connect({ params });

            callRef.current.on('accept', () => {
                setIsInCall(true);
                setCallStatus('Calls.InCall');
            });
            callRef.current.on('disconnect', () => {
                setIsInCall(false);
                hangUp();
            });
            callRef.current.on('error', (error) => {
                setCallStatus('Error: ' + removeTwilioFromMessage(error.message));
            });
        }
    };

    const hangUp = () => {
        try {
            setCallStatus('Calls.RegisteredDevice');
            if (deviceRef.current?.disconnectAll) {
                deviceRef.current.disconnectAll();
            }
        } catch (err) {
            if (err instanceof Error) {
                setCallStatus('Error: ' + removeTwilioFromMessage(err.message));
            }
        }
    };

    const getCallerIdOptions = () => {
        if (!store?.areas) return null;
        const { success, phone, callerId } = handleGetPhoneAgencies({
            area: SERVICE_LEAD_TYPE,
            areas: store.areas || [],
        });
        if (!success) return null;

        const callerIdFormatted = formatPhones(callerId || '');
        const storeFormattedPhone = formatPhones(phone || '');
        const agentFormattedPhone = client?.agent ? formatPhones(client.agent.phone || '') : '';
        const currentUserFormattedPhone = formatPhones(user?.phone || '');

        return (
            <Fragment>
                <option value={callerIdFormatted || storeFormattedPhone}>
                    {t('Calls.CallStore')}
                </option>
                {user?.isVerifiedCallerId && (
                    <option value={currentUserFormattedPhone}>
                        {t('Calls.CallUser')}
                    </option>
                )}
                {client?.agent?.isVerifiedCallerId && (
                    <option value={agentFormattedPhone}>
                        {t('Calls.CallAgent')}
                    </option>
                )}
            </Fragment>
        );
    };

    if (!store) return null;
    if (!callToken) return null;

    return (
        <Card
            intent="section"
            title={`${t('Leads.CallStatus')}: ${t(callStatus)}`}
            {...props}
        >
            <Box mt={2}>
                <TextField
                    fullWidth
                    style={{ marginBottom: '1em' }}
                    name="type"
                    onChange={(e) => setTypeCall(e.target.value)}
                    select
                    variant="outlined"
                    SelectProps={{ native: true }}
                >
                    {types.map((item) => (
                        <option key={item} value={item}>
                            {t(`RecordingTests.${item}`)}
                        </option>
                    ))}
                </TextField>
                {(client?.agent?.isVerifiedCallerId || user?.isVerifiedCallerId) && (
                    <TextField
                        fullWidth
                        style={{ marginBottom: '1em' }}
                        onChange={(e) => setCurrentCallerId(e.target.value)}
                        select
                        variant="outlined"
                        SelectProps={{ native: true }}
                        value={currentCallerId}
                    >
                        {getCallerIdOptions()}
                    </TextField>
                )}
                <Button
                    disabled={callStatus !== 'Calls.RegisteredDevice' || isInCall}
                    variant="contained"
                    type="button"
                    size="large"
                    fullWidth
                    color="primary"
                    startIcon={<CallIcon />}
                    onClick={callCustomer}
                    style={{
                        backgroundColor: theme.palette.success.main,
                        height: 60,
                    }}
                >
                    {t('Leads.MakeCall')}
                </Button>
                <Button
                    disabled={!isInCall}
                    variant="contained"
                    type="button"
                    size="large"
                    fullWidth
                    color="primary"
                    startIcon={<PhoneMissedIcon />}
                    onClick={hangUp}
                    style={{
                        marginTop: '1em',
                        backgroundColor: theme.palette.error.main,
                        height: 60,
                    }}
                >
                    {t('Leads.HangUp')}
                </Button>
            </Box>
        </Card>
    );
};
